import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'

/**
 * One title's newsletter identity.
 *
 * What this saves is what the public sign-up band renders — the name, the
 * send day and the three promise lines (each one can be switched off the
 * band). Until somebody saves here, those
 * are the shipped placeholders that were never signed off.
 */

// The api's MAX_PROMISES: the band is laid out for three.
const MAX_POINTS = 3

const WEEKDAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
]

type Point = { label: string; text: string; shown: boolean }
type Settings = {
  publication: string
  name: string
  send_day: string
  points: Point[]
  // The resolved value — what the public band and the index screen act
  // on right now, whichever of the row or the environment decided it.
  enabled: boolean
  // The row's raw column: null means "nobody has overridden this title,
  // it follows NEWSLETTER_PUBS". A concrete boolean is an operator's
  // decision that beats the environment in either direction.
  enabled_override: boolean | null
  list_address: string
  configured: boolean
}

// Only the fields this screen owns. Sent narrow on purpose: the API
// now rejects anything else (`configured`, `points`, `publication` —
// the shape the GET response comes back in) with a 422, so a save can
// never again round-trip a resolved value back in as if it were an
// operator's explicit override.
type SavePayload = {
  name: string
  send_day: string
  promises: Point[]
  enabled: boolean | null
  list_address: string
}

const inputClass =
  'w-full bg-ink-900 border border-ink-500 px-2.5 py-1.5 font-mono text-[12px] text-paper focus:outline-none focus:border-amber-glow/60'
const labelClass = 'label block mb-1.5'

export function NewsletterSettingsPage() {
  const { pub = '' } = useParams()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['newsletter-settings', pub],
    queryFn: () => api.get<Settings>(`/api/admin/newsletters/${pub}/settings`),
  })

  // Local editable copy of the fetched settings. Synced during render (not
  // an effect) whenever the query returns data this form hasn't seen yet —
  // `loadedFor` tracks which publication the current `form` belongs to, so
  // switching titles or a background refetch both re-sync correctly.
  const [form, setForm] = useState<Settings | null>(null)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  if (data && loadedFor !== pub) {
    setForm(data)
    setLoadedFor(pub)
  }

  const save = useMutation({
    mutationFn: (body: SavePayload) =>
      api.put<Settings>(`/api/admin/newsletters/${pub}/settings`, body),
    onSuccess: (updated) => {
      setForm(updated)
      qc.setQueryData(['newsletter-settings', pub], updated)
      qc.invalidateQueries({ queryKey: ['newsletters'] })
    },
  })

  if (isLoading || !form) {
    return <div className="label text-paper-trace p-6">Loading…</div>
  }

  const setPoint = (i: number, patch: Partial<Point>) =>
    setForm({
      ...form,
      points: form.points.map((p, n) => (n === i ? { ...p, ...patch } : p)),
    })
  const addPoint = () =>
    setForm({ ...form, points: [...form.points, { label: '', text: '', shown: true }] })
  const deletePoint = (i: number) =>
    setForm({ ...form, points: form.points.filter((_, n) => n !== i) })
  // The band shows a line's label and text side by side: both are required.
  const blankLine = form.points.some((p) => !p.label.trim() || !p.text.trim())

  return (
    <div className="p-8 space-y-6 max-w-[900px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="mb-2"><PubBadge slug={form.publication} /></div>
          <h1 className="font-serif text-[36px] leading-[1.05] tracking-tight text-paper">
            {form.name}
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">settings</span>
          </h1>
        </div>
      </header>

      {!form.configured && (
        <div className="border border-amber-glow/40 bg-amber-glow/5 px-4 py-3 text-[13px] text-amber-glow">
          These are the shipped defaults. Nobody has set them for this title yet.
        </div>
      )}

      <Panel label="Identity">
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Newsletter name</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="flex gap-6">
            <div className="w-48">
              <label className={labelClass}>Send day</label>
              <select
                className={inputClass}
                value={form.send_day}
                onChange={(e) => setForm({ ...form, send_day: e.target.value })}
              >
                {WEEKDAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div className="flex-1">
              <label className={labelClass}>Mailgun list address</label>
              <input
                className={inputClass}
                value={form.list_address}
                onChange={(e) => setForm({ ...form, list_address: e.target.value })}
                placeholder="fj-newsletter@membership.flightjournal.com"
              />
            </div>
          </div>

          <div className="w-96">
            <label className={labelClass}>Sign-up band</label>
            <select
              className={inputClass}
              value={form.enabled_override === null ? '' : String(form.enabled_override)}
              onChange={(e) => {
                const v = e.target.value
                setForm({
                  ...form,
                  enabled_override: v === '' ? null : v === 'true',
                })
              }}
            >
              <option value="">Follow the environment default</option>
              <option value="true">On — show the sign-up band on this title's home page</option>
              <option value="false">Off — hide it, whatever the environment says</option>
            </select>
            {form.enabled_override === null && (
              <p className="mt-1.5 font-mono text-[11px] text-paper-dim">
                Currently {form.enabled ? 'live' : 'dark'} via the environment default.
              </p>
            )}
          </div>
        </div>
      </Panel>

      <Panel label="What the band promises">
        <div className="space-y-3">
          <p className="font-mono text-[11px] text-paper-dim">
            Untick a line to leave it off the home page band. It stays here to switch back on.
          </p>
          {form.points.map((p, i) => (
            <div key={i} className={`flex gap-3 items-center ${p.shown ? '' : 'opacity-50'}`}>
              <label className="flex items-center gap-1.5 font-mono text-[11px] text-paper-dim shrink-0 w-16">
                <input
                  type="checkbox"
                  checked={p.shown}
                  onChange={(e) => setPoint(i, { shown: e.target.checked })}
                  aria-label={`Show "${p.label}" on the band`}
                />
                {p.shown ? 'On' : 'Off'}
              </label>
              <input
                className={`${inputClass} w-48`}
                value={p.label}
                onChange={(e) => setPoint(i, { label: e.target.value })}
                placeholder="Label"
              />
              <input
                className={inputClass}
                value={p.text}
                onChange={(e) => setPoint(i, { text: e.target.value })}
                placeholder="Text"
              />
              <button
                type="button"
                onClick={() => deletePoint(i)}
                className="shrink-0 font-mono text-[11px] text-paper-dim hover:text-radar-red"
                aria-label={`Delete "${p.label || 'this line'}"`}
              >
                Delete
              </button>
            </div>
          ))}
          {form.points.length === 0 && (
            <p className="font-mono text-[11px] text-paper-dim">
              No lines: the band shows its title and sign-up box only.
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={form.points.length >= MAX_POINTS}
              onClick={addPoint}
            >
              Add a line
            </Button>
            <span className="font-mono text-[11px] text-paper-dim">
              {form.points.length >= MAX_POINTS
                ? `The band holds ${MAX_POINTS} lines at most.`
                : `Up to ${MAX_POINTS} lines.`}
            </span>
          </div>
          {blankLine && (
            <p role="alert" className="font-mono text-[11px] text-amber-glow">
              Every line needs a label and text before you save. Delete a line you don't want.
            </p>
          )}
        </div>
      </Panel>

      <div className="flex items-center gap-3">
        <Button
          disabled={save.isPending || blankLine}
          onClick={() => save.mutate({
            name: form.name,
            send_day: form.send_day,
            promises: form.points,
            enabled: form.enabled_override,
            list_address: form.list_address,
          })}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {save.isError && (
          <span role="alert" className="font-mono text-[11px] text-radar-red">
            Could not save. Check the send day and the lines, then try again.
          </span>
        )}
        {save.isSuccess && !save.isPending && (
          <span className="font-mono text-[11px] text-radar-green">Saved</span>
        )}
      </div>
    </div>
  )
}
